import { ChangeDetectionStrategy, Component, EventEmitter, Input, OnChanges, Output, SimpleChanges } from '@angular/core';

import { ChannelType, deepCopy, Group, isArray, Skill, Team } from '@contact/upgrade';

import { ITargetSelectionCsr } from '../../transfer/models/transfer';

@Component({
  selector: 'cng-appointment-target-form',
  templateUrl: 'appointment-target-form.component.html',
  styleUrls: ['./appointment-target-form.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class AppointmentTargetFormComponent implements OnChanges {
  @Input() skills: Skill[] = [];
  @Input() justSkills: boolean = false;
  @Input() showErrors: boolean;
  @Input() htmlPrefix: string;
  @Input() originalTeams: Team[];
  @Input() originalGroups: Group[];
  @Input() teams: Team[] = [];
  @Input() groups: Group[] = [];
  @Input() noFieldset: boolean = false;
  @Input() preselectedSkillUuids: string[] | null | undefined;
  @Input() preselectedTeamUuid: string | null | undefined;
  @Input() preselectedGroupUuid: string | null | undefined;
  @Input() preselectedCsrUuid: string | string[] | null | undefined;
  @Input() csrs: ITargetSelectionCsr[] = [];
  @Input() multipleCsrEnabled: boolean = false;
  @Output() eventUpdateCandidateCsr: EventEmitter<any>;
  @Output() eventTargetChange: EventEmitter<
    { skills: Skill[] | undefined; team: Team | undefined; group: Group | undefined; csr: any | any[] | undefined } | undefined
  >;

  // template related switches
  public preselectedSkills: Skill[] | null | undefined;
  public skillsResetSwitch: boolean = false;
  public teamResetSwitch: boolean = false;
  public groupResetSwitch: boolean = false;
  public csrResetSwitch: boolean = false;
  public preselectedCsr: any | any[] | null | undefined;
  public preselectedTeam: Team | null | undefined;
  public preselectedGroup: Group | null | undefined;

  // internal properties
  public ChannelType = ChannelType;
  private preselectedSkillsFiltered: boolean = false;
  private preselectedTeamFiltered: boolean = false;
  private preselectedGroupFiltered: boolean = false;
  private preSelectedCsrFiltered: boolean = false;

  // internal properties
  public selectedSkills: Skill[] | undefined = [];
  public selectedTeam: Team | undefined;
  public selectedGroup: Group | undefined;
  public skillIsValid = false;
  public htmlidPopupCsrSkillSelection: string;
  public htmlidPopupCsrGroupSelection: string;
  public htmlidPopupCsrTeamSelection: string;
  public htmlidPopupCsrSelection: string;

  constructor() {
    this.htmlidPopupCsrSkillSelection = `${this.htmlPrefix}_htmlid_popupCsrSkillSelection`;
    this.htmlidPopupCsrGroupSelection = `${this.htmlPrefix}_htmlid_popupCsrGroupSelection`;
    this.htmlidPopupCsrTeamSelection = `${this.htmlPrefix}_htmlid_popupCsrTeamSelection`;
    this.htmlidPopupCsrSelection = `${this.htmlPrefix}_htmlid_popupCsrSelection`;
    this.eventTargetChange = new EventEmitter();
    this.eventUpdateCandidateCsr = new EventEmitter();
  }

  ngOnChanges(changes: SimpleChanges): void {
    const {
      ['skills']: skills,
      ['preselectedSkillUuids']: preselectedSkillUuids,
      ['teams']: teams,
      ['preselectedTeamUuid']: preselectedTeamUuid,
      ['groups']: groups,
      ['preselectedGroupUuid']: preselectedGroupUuid,
      ['csrs']: csrs,
      ['preselectedCsrUuid']: preselectedCsrUuid,
    } = changes;

    this.skillIsValid = !!this.selectedSkills && this.selectedSkills.length > 0;

    if (!!skills) {
      this.skills = deepCopy(skills.currentValue);
      if (!this.preselectedSkillsFiltered) {
        this.filterPreselectedSkills();
      }
    }

    if (!!preselectedSkillUuids) {
      this.preselectedSkillUuids = deepCopy(preselectedSkillUuids.currentValue);
      if (!this.preselectedSkillsFiltered) {
        this.filterPreselectedSkills();
      }
    }

    if (!!teams) {
      this.teams = deepCopy(teams.currentValue);
      if (!this.preselectedTeamFiltered) {
        this.filterPreselectedTeam();
      }
    }
    if (!!preselectedTeamUuid) {
      this.preselectedTeamUuid = preselectedTeamUuid.currentValue;
      if (!this.preselectedTeamFiltered) {
        this.filterPreselectedTeam();
      }
    }
    if (!!groups) {
      this.groups = deepCopy(groups.currentValue);
      if (!this.preselectedGroupFiltered) {
        this.filterPreselectedGroup();
      }
    }
    if (!!preselectedGroupUuid) {
      this.preselectedGroupUuid = preselectedGroupUuid.currentValue;
      if (!this.preselectedGroupFiltered) {
        this.filterPreselectedGroup();
      }
    }
    if (!!csrs) {
      this.csrs = deepCopy(csrs.currentValue);
      if (!this.preSelectedCsrFiltered) {
        this.filterPreselectedCsr();
      }
    }
    if (!!preselectedCsrUuid) {
      this.preselectedCsrUuid = deepCopy(preselectedCsrUuid.currentValue);
      if (!this.preSelectedCsrFiltered) {
        this.filterPreselectedCsr();
      }
    }
  }

  private filterPreselectedCsr() {
    this.preselectedCsr = undefined;
    this.preSelectedCsrFiltered = true;

    if (!!this.csrs && !!this.preselectedCsrUuid) {
      let csrsFound: ITargetSelectionCsr | undefined;
      if (isArray(this.preselectedCsrUuid)) {
        const csrs = this.csrs.filter(csr => (this.preselectedCsrUuid as string[]).some(uuid => uuid === csr.uuid));
        if (!!csrs && csrs.length > 0) {
          csrsFound = csrs[0];
        }
      } else {
        csrsFound = this.csrs.find(csr => csr.uuid === this.preselectedCsrUuid);
      }
      this.preselectedCsr = deepCopy(csrsFound);
    }
  }

  private filterPreselectedGroup() {
    if (!!this.groups && !!this.preselectedGroupUuid) {
      this.preselectedGroup = deepCopy(this.groups.find(group => group.id === this.preselectedGroupUuid));
      this.preselectedGroupFiltered = true;
    } else {
      this.preselectedGroup = undefined;
    }
  }

  private filterPreselectedTeam() {
    if (!!this.teams && this.preselectedTeamUuid) {
      this.preselectedTeam = deepCopy(this.teams.find(team => team.id === this.preselectedTeamUuid));
      this.preselectedTeamFiltered = true;
    } else {
      this.preselectedTeam = undefined;
    }
  }

  private filterPreselectedSkills() {
    if (!!this.preselectedSkillUuids && !!this.skills) {
      this.preselectedSkills = deepCopy(this.skills.filter(skill => (this.preselectedSkillUuids as string[]).some(id => id === skill.id)));
      this.preselectedSkillsFiltered = true;
    } else {
      this.preselectedSkills = [];
    }
  }

  updateCandidateCsr(filterOnlyBySkills: boolean = false, csrPrefix: string = ''): void {
    let teamId: string | null = null;
    let groupId: string | null = null;
    if (!filterOnlyBySkills) {
      teamId = this.selectedTeam ? this.selectedTeam.id : '';
      groupId = this.selectedGroup ? this.selectedGroup.id : '';
    }
    const skillIds: string[] | null = this.selectedSkills ? this.selectedSkills.map(skill => skill.id) : null;
    const filter: any = {
      teamId,
      groupId,
      skillIds,
      csrPrefix,
    };
    this.eventUpdateCandidateCsr.emit(filter);
  }

  onSkillSelected(skills: Skill[] | undefined): void {
    this.selectedSkills = skills;
    this.groupResetSwitch = !this.groupResetSwitch;
    this.teamResetSwitch = !this.teamResetSwitch;
    this.eventTargetChange.emit({ skills: this.selectedSkills, team: undefined, group: undefined, csr: undefined });
    this.updateCandidateCsr(false);
  }

  onTeamSelected(team: Team | undefined): void {
    this.selectedTeam = team;
    this.updateCandidateCsr(false);
  }

  onGroupSelected(group: Group | undefined) {
    this.selectedGroup = group;
    this.updateCandidateCsr(false);
  }

  onCsrSelected(_csr: any | any[] | undefined) {
    this.eventTargetChange.emit({ skills: this.selectedSkills, team: this.selectedTeam, group: this.selectedGroup, csr: _csr });
  }

  resetForm(): void {
    this.csrResetSwitch = true;
    this.groupResetSwitch = true;
    this.teamResetSwitch = true;

    this.preselectedTeam = this.preselectedGroup = this.preselectedCsr = undefined;
  }

  isTeamOrGroupSelectable(): boolean {
    return !!this.selectedSkills && this.selectedSkills.length > 0;
  }

  isCsrSelectable(): boolean {
    return !!this.csrs && !!this.selectedSkills && this.selectedSkills.length > 0;
  }
}
